import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { clientEfFoutTekst, clientEfVerwijderFoutTekst } from '../../utils/clientAanmaakFouten.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';

const ROLES = ['superadmin', 'admin_coach', 'coach', 'client'];
const STAF_ROLLEN = ['coach', 'admin_coach'];

/**
 * Mag de ingelogde beheerder dit staf-account anonimiseren? Spiegelt exact de
 * server-side regels in `delete_client_account()` — die blijft leidend; dit
 * voorkomt alleen dat er een knop verschijnt die gegarandeerd faalt.
 */
function magStafVerwijderen(doelgebruiker, eigenRol, eigenId) {
  if (!STAF_ROLLEN.includes(doelgebruiker.role)) return false;
  if (doelgebruiker.deleted_at) return false;
  if (doelgebruiker.id === eigenId) return false;
  if (!['admin_coach', 'superadmin'].includes(eigenRol)) return false;
  if (doelgebruiker.role === 'admin_coach' && eigenRol !== 'superadmin') return false;
  return true;
}
const ROLE_LABELS = {
  superadmin: t('gebruikersBeheer.rolSuperadmin'),
  admin_coach: t('gebruikersBeheer.rolAdminCoach'),
  coach: t('gebruikersBeheer.rolCoach'),
  client: t('gebruikersBeheer.rolClient'),
};

function EditModal({ user: editUser, onClose, onSaved, currentRole }) {
  const [role, setRole] = useState(editUser.role || 'client');
  const [coachId, setCoachId] = useState('');
  const [clientStatus, setClientStatus] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadData() {
      const [coachRes, ccRes, profileRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach', 'superadmin']).is('deleted_at', null).order('full_name'),
        supabase.from('coach_clients').select('coach_id').eq('client_id', editUser.id).eq('is_primary', true).maybeSingle(),
        supabase.from('profiles').select('client_status').eq('id', editUser.id).maybeSingle(),
      ]);
      setCoaches(coachRes.data || []);
      setCoachId(ccRes.data?.coach_id || '');
      setClientStatus(profileRes.data?.client_status || 'active');
    }
    loadData();
  }, [editUser.id]);

  const allowedRoles = currentRole === 'superadmin'
    ? ROLES
    : ROLES.filter(r => r !== 'superadmin' && r !== 'admin_coach');

  // Deze koppelingsmutaties liepen zonder enige foutafhandeling: noch de
  // error, noch het aantal geraakte rijen werd bekeken. Een UPDATE die door
  // RLS 0 rijen raakt geeft géén fout (BRIEFING hoofdstuk 8), dus de
  // gebruiker kreeg "opgeslagen" terwijl de primaire coach ongewijzigd bleef
  // — en bij een halve doorloop (eerste update wél, tweede niet) houdt de
  // cliënt zelfs helemáál geen primaire coach meer over. Nu breekt elke
  // mislukte stap de hele handeling af met een zichtbare melding.
  async function setPrimary(coachClientId, isPrimary) {
    const { data, error: err } = await supabase
      .from('coach_clients')
      .update({ is_primary: isPrimary })
      .eq('id', coachClientId)
      .select('id')
      .maybeSingle();
    if (err) throw err;
    if (!data) throw new Error(t('common.wijzigingNietOpgeslagen'));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (role !== editUser.role) {
        const { error: roleErr } = await supabase.rpc('set_user_role', {
          target_user_id: editUser.id,
          new_role: role,
        });
        if (roleErr) throw roleErr;
      }

      if (role === 'client' && coachId) {
        const { data: existing } = await supabase
          .from('coach_clients')
          .select('id, coach_id, is_primary')
          .eq('client_id', editUser.id)
          .eq('is_primary', true)
          .maybeSingle();

        if (existing && existing.coach_id !== coachId) {
          await setPrimary(existing.id, false);
          const { data: newCc } = await supabase
            .from('coach_clients')
            .select('id')
            .eq('client_id', editUser.id)
            .eq('coach_id', coachId)
            .maybeSingle();
          if (newCc) {
            await setPrimary(newCc.id, true);
          } else {
            const { error: insErr } = await supabase.from('coach_clients').insert({
              client_id: editUser.id,
              coach_id: coachId,
              status: 'active',
              is_primary: true,
            });
            if (insErr) throw insErr;
          }
        } else if (!existing && coachId) {
          const { data: existingCoach } = await supabase
            .from('coach_clients')
            .select('id')
            .eq('client_id', editUser.id)
            .eq('coach_id', coachId)
            .maybeSingle();
          if (existingCoach) {
            await setPrimary(existingCoach.id, true);
          } else {
            const { error: insErr } = await supabase.from('coach_clients').insert({
              client_id: editUser.id,
              coach_id: coachId,
              status: 'active',
              is_primary: true,
            });
            if (insErr) throw insErr;
          }
        }
      }

      if (role === 'client' && clientStatus) {
        const { data: statusRow, error: statusErr } = await supabase
          .from('profiles')
          .update({ client_status: clientStatus })
          .eq('id', editUser.id)
          .select('id')
          .maybeSingle();
        if (statusErr) throw statusErr;
        if (!statusRow) throw new Error(t('common.wijzigingNietOpgeslagen'));
      }

      onSaved();
    } catch (err) {
      setError(t('gebruikersBeheer.opslaanFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('gebruikersBeheer.bewerkTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSave}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelNaam')}</label>
            <input className="form-input" value={editUser.full_name || ''} disabled />
          </div>

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelEmail')}</label>
            <input className="form-input" value={editUser.email || ''} disabled />
          </div>

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelRol')}</label>
            <select className="form-input" value={role} onChange={e => setRole(e.target.value)}>
              {allowedRoles.map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>

          {role === 'client' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('gebruikersBeheer.labelStatus')}</label>
                <select className="form-input" value={clientStatus} onChange={e => setClientStatus(e.target.value)}>
                  <option value="active">{t('gebruikersBeheer.statusActive')}</option>
                  <option value="prospect">{t('gebruikersBeheer.statusProspect')}</option>
                  <option value="inactive">{t('gebruikersBeheer.statusInactive')}</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('gebruikersBeheer.labelCoach')}</label>
                <select className="form-input" value={coachId} onChange={e => setCoachId(e.target.value)}>
                  <option value="">{t('gebruikersBeheer.geenCoach')}</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {t('gebruikersBeheer.annuleren')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('gebruikersBeheer.bezig') : t('gebruikersBeheer.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated, currentRole }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('client');
  const [coachId, setCoachId] = useState('');
  const [clientStatus, setClientStatus] = useState('active');
  const [coaches, setCoaches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdPassword, setCreatedPassword] = useState(null);

  useEffect(() => {
    supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach', 'superadmin']).is('deleted_at', null).order('full_name')
      .then(({ data }) => setCoaches(data || []));
  }, []);

  const allowedRoles = currentRole === 'superadmin'
    ? ROLES
    : ROLES.filter(r => r !== 'superadmin' && r !== 'admin_coach');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) { setError(t('gebruikersBeheer.validatieNaam')); return; }
    if (!email.trim()) { setError(t('gebruikersBeheer.validatieEmail')); return; }
    if (!role) { setError(t('gebruikersBeheer.validatieRol')); return; }
    // create-client eist een coach (zonder koppeling is de cliënt onzichtbaar);
    // hier client-side afvangen scheelt een onnodige serveraanroep. Geldt
    // uitsluitend voor rol 'client' — staf heeft geen coach-koppeling.
    if (role === 'client' && !coachId) { setError(t('gebruikersBeheer.validatieCoach')); return; }

    setSaving(true);
    try {
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession?.access_token) throw new Error(t('gebruikersBeheer.geenSessie'));

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshSession.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // De rol gaat mee naar de server: create-client past daar zijn
          // cliënt-specifieke eisen (coach-koppeling, abonnement) op aan en
          // zet het profiel meteen op de juiste rol. Een losse set_user_role
          // achteraf is daardoor niet meer nodig (die liet de nieuwe coach
          // eerst als cliënt bestaan).
          role,
          email: email.trim(),
          full_name: fullName.trim(),
          coach_id: role === 'client' ? (coachId || null) : null,
          client_status: role === 'client' ? clientStatus : 'active',
          membership_type_id: null,
          membership_start_date: null,
        }),
      });

      const result = await response.json();
      if (!response.ok || result.error) throw new Error(clientEfFoutTekst(result));

      // Wachtwoord wordt maar één keer getoond (staat nergens anders opgeslagen)
      // — de lijst pas verversen/modal sluiten zodra de admin dit heeft gezien.
      setCreatedPassword(result.password);
    } catch (err) {
      setError(t('gebruikersBeheer.aanmakenFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (createdPassword) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 440 }}>
          <div className="modal-header">
            <h2 className="modal-title">{t('gebruikersBeheer.wachtwoordTonenTitel')}</h2>
          </div>
          <p>{t('gebruikersBeheer.wachtwoordTonenUitleg', { naam: fullName.trim() })}</p>
          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelWachtwoord')}</label>
            <input className="form-input" readOnly value={createdPassword} style={{ fontFamily: 'monospace', fontSize: 16 }} onFocus={e => e.target.select()} />
          </div>
          <p className="text-muted" style={{ fontSize: 13 }}>{t('gebruikersBeheer.wachtwoordTonenNietGemaild')}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-primary" onClick={async () => { await onCreated(); }}>
              {t('gebruikersBeheer.wachtwoordTonenSluiten')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('gebruikersBeheer.aanmakenTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelNaam')} *</label>
            <input className="form-input" value={fullName} onChange={e => setFullName(e.target.value)} autoFocus />
          </div>

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelEmail')} *</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">{t('gebruikersBeheer.labelRol')} *</label>
            <select className="form-input" value={role} onChange={e => setRole(e.target.value)}>
              {allowedRoles.map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>

          {role === 'client' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('gebruikersBeheer.labelStatus')}</label>
                <select className="form-input" value={clientStatus} onChange={e => setClientStatus(e.target.value)}>
                  <option value="active">{t('gebruikersBeheer.statusActive')}</option>
                  <option value="prospect">{t('gebruikersBeheer.statusProspect')}</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('gebruikersBeheer.labelCoach')}</label>
                <select className="form-input" value={coachId} onChange={e => setCoachId(e.target.value)}>
                  <option value="">{t('gebruikersBeheer.geenCoach')}</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {t('gebruikersBeheer.annuleren')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('gebruikersBeheer.bezig') : t('gebruikersBeheer.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function GebruikersBeheerPage() {
  const { role, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteStaf, setDeleteStaf] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_users');
      if (rpcErr) throw rpcErr;

      const clientIds = (data || []).filter(u => u.role === 'client').map(u => u.id);
      let coachMap = {};
      let statusMap = {};

      if (clientIds.length > 0) {
        const [ccRes, profRes] = await Promise.all([
          supabase
            .from('coach_clients')
            .select('client_id, coach:profiles!coach_clients_coach_id_fkey(full_name)')
            .eq('is_primary', true)
            .in('client_id', clientIds),
          supabase
            .from('profiles')
            .select('id, client_status')
            .in('id', clientIds),
        ]);
        (ccRes.data || []).forEach(cc => {
          if (cc.coach) coachMap[cc.client_id] = cc.coach.full_name;
        });
        (profRes.data || []).forEach(p => {
          statusMap[p.id] = p.client_status;
        });
      }

      setUsers((data || []).map(u => ({
        ...u,
        coachName: coachMap[u.id] || null,
        client_status: statusMap[u.id] || null,
      })));
    } catch (err) {
      setError(t('gebruikersBeheer.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (roleFilter === 'all') return true;
    if (roleFilter === 'prospect') return u.role === 'client' && u.client_status === 'prospect';
    if (roleFilter === 'client') return u.role === 'client' && u.client_status !== 'prospect';
    if (roleFilter === 'coach') return ['coach', 'admin_coach', 'superadmin'].includes(u.role);
    return true;
  });

  async function handleSaved() {
    setEditUser(null);
    showSuccess(t('gebruikersBeheer.opslaanSucces'));
    await load();
  }

  async function handleCreated() {
    setShowCreate(false);
    showSuccess(t('gebruikersBeheer.aanmakenSucces'));
    await load();
  }

  /**
   * Staf-account anonimiseren. Bewust dezelfde Edge Function als de
   * cliënt-flow (`delete-client-account`): die roept de rolbewuste RPC
   * `delete_client_account` aan en blokkeert daarna het inloggen. Er wordt
   * NOOIT hard verwijderd — gekoppelde lessen, afspraken en administratie
   * moeten bewaard blijven.
   */
  async function handleDeleteStaf() {
    if (!deleteStaf) return;
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('delete-client-account', {
        body: { client_id: deleteStaf.id },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (res.error) {
        // Bij een non-2xx zit de body in error.context, niet in res.data.
        let body = null;
        try { body = await res.error.context?.json(); } catch { /* geen JSON-body */ }
        throw new Error(body?.error
          ? clientEfVerwijderFoutTekst(body)
          : (res.error.message || t('clients.efFoutVerwijderOnbekend')));
      }
      // De EF antwoordt bij een RPC-fout bewust met status 200 + { error }.
      if (res.data?.error) throw new Error(clientEfVerwijderFoutTekst(res.data));

      if (res.data?.login_geblokkeerd === false) {
        showError(t('gebruikersBeheer.verwijderLoginNietGeblokkeerd'));
      } else {
        showSuccess(t('gebruikersBeheer.verwijderSucces'));
      }
      setDeleteStaf(null);
      await load();
    } catch (err) {
      showError(t('gebruikersBeheer.verwijderFout') + err.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      {editUser && (
        <EditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={handleSaved}
          currentRole={role}
        />
      )}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          currentRole={role}
        />
      )}
      {deleteStaf && (
        <ConfirmModal
          danger
          title={t('gebruikersBeheer.verwijderTitel')}
          body={t('gebruikersBeheer.verwijderTekst', { naam: deleteStaf.full_name || '—' })}
          confirmLabel={deleting ? t('gebruikersBeheer.bezig') : t('gebruikersBeheer.verwijderBevestig')}
          onConfirm={handleDeleteStaf}
          onClose={() => { if (!deleting) setDeleteStaf(null); }}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('gebruikersBeheer.title')}</h1>
          <p className="page-subtitle">{filtered.length} {t('clients.aantalGevonden')}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            {t('gebruikersBeheer.nieuweGebruiker')}
          </button>
          <input
            type="search"
            className="form-input"
            placeholder={t('gebruikersBeheer.zoekPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 0, maxWidth: 260 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {['all', 'client', 'prospect', 'coach'].map(f => (
          <button
            key={f}
            className={roleFilter === f ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setRoleFilter(f)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {t(`gebruikersBeheer.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('gebruikersBeheer.colNaam')}</th>
                  <th>{t('gebruikersBeheer.colEmail')}</th>
                  <th>{t('gebruikersBeheer.colRol')}</th>
                  <th>{t('gebruikersBeheer.colStatus')}</th>
                  <th>{t('gebruikersBeheer.colCoach')}</th>
                  <th style={{ textAlign: 'right' }}>{t('gebruikersBeheer.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state">
                        <p>{t('gebruikersBeheer.geenResultaat')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(u => (
                    <tr key={u.id}>
                      <td className="font-medium">{u.full_name || '—'}</td>
                      <td className="text-muted">{u.email || '—'}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: u.role === 'superadmin' ? '#fef3c7' : u.role === 'admin_coach' ? '#dbeafe' : u.role === 'coach' ? '#dcfce7' : '#f3f4f6',
                          color: u.role === 'superadmin' ? '#92400e' : u.role === 'admin_coach' ? '#1e40af' : u.role === 'coach' ? '#166534' : '#374151',
                        }}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="text-muted">
                        {u.deleted_at && (
                          <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 12 }}>{t('gebruikersBeheer.statusVerwijderd')}</span>
                        )}
                        {!u.deleted_at && u.role === 'client' && u.client_status === 'prospect' && (
                          <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>{t('gebruikersBeheer.statusProspect')}</span>
                        )}
                        {!u.deleted_at && u.role === 'client' && u.client_status !== 'prospect' && (
                          <span style={{ color: '#16a34a', fontSize: 12 }}>{t('gebruikersBeheer.statusActive')}</span>
                        )}
                        {!u.deleted_at && u.role !== 'client' && '—'}
                      </td>
                      <td className="text-muted">{u.coachName || '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!u.deleted_at && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditUser(u)}>
                            {t('gebruikersBeheer.bewerken')}
                          </button>
                        )}
                        {magStafVerwijderen(u, role, user?.id) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#dc2626' }}
                            onClick={() => setDeleteStaf(u)}
                          >
                            {t('gebruikersBeheer.verwijderen')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
